<template>
  <b-modal
      :visible="visible"
      size="lg"
      hide-footer
      centered
      :title="title"
      @change="$emit('change', $event)"
  >
    <b-overlay :show="isLoading" rounded="sm">
      <vue-apex-charts
          v-if="hasPoints"
          type="line"
          height="350"
          :options="chartOptions"
          :series="series"
      />
      <div v-else-if="isError" class="text-center text-danger py-4">
        Ошибка загрузки истории
      </div>
      <div v-else-if="!isLoading" class="text-center text-muted py-4">
        Нет данных истории
      </div>
    </b-overlay>
  </b-modal>
</template>

<script>
import { BModal, BOverlay } from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import { ref, computed, watch } from '@vue/composition-api'
import { $themeColors } from '@themeConfig'
import { formatNumber } from '@core/utils/filter'
import store from '@/store'

export default {
  components: { BModal, BOverlay, VueApexCharts },
  props: {
    visible: { type: Boolean, default: false },
    movieId: { type: [String, Number], required: true },
    // 'rating' — график рейтингов, 'votes' — график количества оценок
    mode: { type: String, default: 'rating' },
  },
  model: { prop: 'visible', event: 'change' },
  setup(props) {
    const isLoading = ref(false)
    const isError = ref(false)
    const points = ref([])

    watch(() => props.visible, visible => {
      // При закрытии сбрасываем данные: график должен монтироваться заново после
      // загрузки, иначе apexcharts меряет размеры до окончания анимации модалки
      if (!visible) {
        points.value = []
        isError.value = false
        return
      }
      isLoading.value = true
      store.dispatch('movies/fetchRatingHistory', { id: props.movieId })
        .then(response => {
          points.value = response.data.data
        })
        .catch(() => {
          isError.value = true
        })
        .finally(() => {
          isLoading.value = false
        })
    })

    const title = computed(() => (props.mode === 'votes' ? 'История количества оценок' : 'История рейтинга'))

    // votes пишутся на бэке только при изменении (иначе NULL) — NULL-точки пропускаем
    const buildSeries = (kpField, imdbField) => [
      {
        name: 'КП',
        data: points.value
          .filter(point => point[kpField] !== null)
          .map(point => ({ x: point.date, y: Number(point[kpField]) })),
      },
      {
        name: 'IMDB',
        data: points.value
          .filter(point => point[imdbField] !== null)
          .map(point => ({ x: point.date, y: Number(point[imdbField]) })),
      },
    ]

    const series = computed(() => (props.mode === 'votes'
      ? buildSeries('votes_kp', 'votes_imdb')
      : buildSeries('rating_kp', 'rating_imdb')))

    const hasPoints = computed(() => series.value.some(oneSeries => oneSeries.data.length > 0))

    const chartOptions = computed(() => ({
      chart: { toolbar: { show: true }, zoom: { enabled: true } },
      colors: [$themeColors.warning, $themeColors.info],
      stroke: { curve: 'straight', width: 2 },
      markers: { size: 4 },
      xaxis: { type: 'datetime' },
      yaxis: props.mode === 'votes'
        ? { labels: { formatter: value => formatNumber(Math.round(value)) } }
        : { min: 0, max: 10, labels: { formatter: value => value.toFixed(1) } },
      tooltip: {
        shared: true,
        intersect: false,
        x: { format: 'dd.MM.yyyy HH:mm' },
      },
      legend: { position: 'top' },
    }))

    return { isLoading, isError, title, series, hasPoints, chartOptions }
  },
}
</script>
